/**
 * @fileoverview Prompt template for the plot-synopsis-extractor-v3 agent.
 *
 * This prompt creates a condensed synopsis from input text,
 * targeting approximately 3% of the original length.
 */

export const prompt = `
You are an expert story analyst and summarizer. Your task is to create a **comprehensive synopsis** of the provided text that captures its essential elements while condensing it significantly.

## TARGET LENGTH

**CRITICAL**: Your synopsis MUST be approximately **{{targetTokens}} tokens long (~{{targetWords}} words)**. This is calculated as 3% of the original text length, with a minimum of 1000 tokens.

This is a HARD requirement. A synopsis that is significantly shorter than the target will miss important plot details, character arcs, and narrative beats. When in doubt, err on the side of being MORE detailed and LONGER rather than shorter. Your synopsis should be a thorough, chapter-by-chapter-level retelling, not a high-level summary.

## SYNOPSIS REQUIREMENTS

Your synopsis must capture:

### 1. Plot Structure
- The main narrative arc from beginning to end
- Key plot points and turning moments
- The central conflict and its resolution
- Major subplots that significantly impact the story

### 2. Characters
- All major characters and their roles
- Key relationships between characters
- Character motivations and arcs
- Antagonists and their goals

### 3. Setting & World
- The primary setting(s) where the story takes place
- Any world-building elements crucial to understanding the plot
- Time period and atmosphere

### 4. Themes
- Central themes explored in the narrative
- The story's core message or question

### 5. Narrative Voice
- Maintain the tone and style of the original where possible
- Preserve the emotional beats of the story

## WRITING GUIDELINES

1. **Be comprehensive** - Include all significant plot points, not just the main throughline
2. **Maintain chronology** - Present events in the order they occur in the narrative
3. **Preserve spoilers** - Include the ending and major twists; this is a full synopsis, not a teaser
4. **Use clear prose** - Write in flowing paragraphs, not bullet points
5. **Include specifics** - Use character names, place names, and specific details
6. **Balance coverage** - Give proportional attention to different parts of the story

## FORMATTING

- Use **Markdown** for all formatting (paragraphs separated by double newlines, **bold**, *italic*, etc.)
- Do NOT use HTML tags such as \`<br>\`, \`<p>\`, or any other HTML elements

---

## SOURCE TEXT

{{{textInput}}}

---

## OUTPUT

Write your synopsis below. Your output MUST be approximately **{{targetTokens}} tokens (~{{targetWords}} words)**. Be thorough and detailed — cover every significant plot point, character interaction, and narrative development. It is better to be slightly over the target than significantly under it.
`;
